import { ethers } from 'ethers';
import { LEVER_SWAP_CONFIG } from './swapConfig';
import { isNativeAddress, safeNormalize } from './swapUtils';

const FLAG_KEYS = [
'allowedTxTarget',
'allowedSpender',
'chainOk',
'tokenInOk',
'tokenOutOk',
'valueOk',
'approvalsOk',
'quoteOk'];


function hasData(data) {
  return typeof data === 'string' && data.startsWith('0x') && data.length > 10;
}

function sameAddress(a, b) {
  if (isNativeAddress(a) && isNativeAddress(b)) return true;
  try {
    return safeNormalize(a).toLowerCase() === safeNormalize(b).toLowerCase();
  } catch {
    return false;
  }
}

function normalizeValue(value) {
  try {
    return BigInt(value || '0');
  } catch {
    return BigInt(0);
  }
}

function flagValue(flags, key) {
  if (!Object.prototype.hasOwnProperty.call(flags, key)) return null;
  if (flags[key] == null) return null;
  return Boolean(flags[key]);
}

function makeResult({ errors = [], warnings = [], flags = {} }) {
  const result = {
    ok: false,
    errors,
    warnings
  };

  FLAG_KEYS.forEach((key) => {
    result[key] = flagValue(flags, key);
  });

  const explicitFalseFlag = FLAG_KEYS.some((key) => result[key] === false);
  result.ok = errors.length === 0 && !explicitFalseFlag;
  return result;
}

export function mergeValidationResults(...results) {
  const errors = [];
  const warnings = [];
  const flags = {};

  results.filter(Boolean).forEach((result) => {
    errors.push(...(Array.isArray(result.errors) ? result.errors : []));
    warnings.push(...(Array.isArray(result.warnings) ? result.warnings : []));

    FLAG_KEYS.forEach((key) => {
      if (result[key] !== undefined && result[key] !== null) {
        flags[key] = flags[key] === undefined ? Boolean(result[key]) : flags[key] && Boolean(result[key]);
      }
    });
  });

  return makeResult({ errors, warnings, flags });
}

export function validatePreparedSwap({ mode, walletAddress, txData, tokenIn, tokenOut, amount }) {
  const warnings = [];
  const errors = [];
  const flags = {
    chainOk: LEVER_SWAP_CONFIG.CHAIN_ID === 10,
    allowedTxTarget: false,
    tokenInOk: false,
    tokenOutOk: false,
    valueOk: false
  };

  if (!['BUY', 'SELL'].includes(mode)) errors.push('Invalid swap mode.');
  if (!walletAddress || !ethers.isAddress(walletAddress)) errors.push('Invalid wallet address.');
  if (!flags.chainOk) errors.push('Configured chain is not Optimism Mainnet (10).');

  try {safeNormalize(tokenIn);} catch {errors.push('Invalid tokenIn.');}
  try {safeNormalize(tokenOut);} catch {errors.push('Invalid tokenOut.');}

  const amountBN = normalizeValue(amount);
  if (amountBN === BigInt(0)) errors.push('Swap amount is empty.');

  if (!txData || typeof txData !== 'object') {
    errors.push('Missing Sushi tx data.');
  } else {
    try {
      const txTarget = safeNormalize(txData.to);
      flags.allowedTxTarget = sameAddress(txTarget, LEVER_SWAP_CONFIG.ROUTER_ADDRESS);
      if (!flags.allowedTxTarget) {
        errors.push(`Sushi tx target is not allowlisted: ${txTarget}`);
      }
    } catch {
      errors.push('Invalid tx.to returned by Sushi API.');
    }

    if (!hasData(txData.data)) errors.push('Sushi API returned empty calldata.');
  }

  const valueBN = normalizeValue(txData?.value);

  if (mode === 'BUY') {
    flags.tokenInOk = sameAddress(tokenIn, LEVER_SWAP_CONFIG.NATIVE_ADDRESS) || sameAddress(tokenIn, LEVER_SWAP_CONFIG.WETH_ADDRESS);
    flags.tokenOutOk = sameAddress(tokenOut, LEVER_SWAP_CONFIG.PR_ADDRESS);
    flags.valueOk = sameAddress(tokenIn, LEVER_SWAP_CONFIG.NATIVE_ADDRESS) ? valueBN > BigInt(0) : valueBN === BigInt(0);

    if (!flags.tokenInOk) errors.push('BUY tokenIn is not native ETH or WETH.');
    if (!flags.tokenOutOk) errors.push('BUY tokenOut is not the StarTax token.');
    if (!flags.valueOk) errors.push('BUY tx.value is not coherent with tokenIn.');
  }

  if (mode === 'SELL') {
    flags.tokenInOk = sameAddress(tokenIn, LEVER_SWAP_CONFIG.PR_ADDRESS);
    flags.tokenOutOk = sameAddress(tokenOut, LEVER_SWAP_CONFIG.NATIVE_ADDRESS);
    flags.valueOk = valueBN === BigInt(0);

    if (!flags.tokenInOk) errors.push('SELL tokenIn is not the StarTax token.');
    if (!flags.tokenOutOk) errors.push('SELL tokenOut is not native ETH.');
    if (!flags.valueOk) errors.push('SELL swap returned non-zero native value.');
  }

  return makeResult({ errors, warnings, flags });
}

export function validateApprovalChecks({ allowanceChecks = [] }) {
  const errors = [];
  const warnings = [];
  const allowedSpenders = [
  LEVER_SWAP_CONFIG.PERMIT2_ADDRESS,
  LEVER_SWAP_CONFIG.ROUTER_ADDRESS]
  .map((address) => safeNormalize(address).toLowerCase());

  let approvalsOk = true;

  allowanceChecks.forEach((check, index) => {
    let spenderOk = false;
    let tokenOk = false;

    try {
      tokenOk = sameAddress(check.tokenAddress, LEVER_SWAP_CONFIG.PR_ADDRESS) ||
      sameAddress(check.tokenAddress, LEVER_SWAP_CONFIG.WETH_ADDRESS);
    } catch {
      tokenOk = false;
    }

    try {
      spenderOk = allowedSpenders.includes(safeNormalize(check.spender).toLowerCase());
    } catch {
      spenderOk = false;
    }

    if (!tokenOk) {
      approvalsOk = false;
      errors.push(`Approval[${index}] token is not allowlisted: ${check.tokenAddress}`);
    }
    if (!spenderOk) {
      approvalsOk = false;
      errors.push(`Approval[${index}] spender is not allowlisted: ${check.spender}`);
    }
  });

  return makeResult({
    errors,
    warnings,
    flags: {
      approvalsOk,
      allowedSpender: approvalsOk
    }
  });
}

export function validateSellQuoteResult(quoteVerification) {
  if (!quoteVerification) {
    return makeResult({
      errors: ['SELL quote verification result is missing.'],
      flags: { quoteOk: false }
    });
  }

  if (!quoteVerification.checked) {
    return makeResult({
      errors: [quoteVerification.reason || 'SELL quote verification was not checked.'],
      flags: { quoteOk: false }
    });
  }

  if (!quoteVerification.ok) {
    return makeResult({
      errors: [quoteVerification.reason || 'SELL quote verification failed.'],
      flags: { quoteOk: false }
    });
  }

  return makeResult({
    warnings: [],
    flags: { quoteOk: true }
  });
}
